import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'

import { OwnerRoute } from './OwnerRoute.js'
import { OwnerShell } from './OwnerShell.js'
import { useApplyOwnerTheme } from './theme/useOwnerTheme.js'
import { LoginPage } from './pages/LoginPage.js'
import { SetupWizardPage } from './pages/SetupWizardPage.js'
import { ResetPasswordPage } from './pages/ResetPasswordPage.js'
import { DashboardPage } from './pages/DashboardPage.js'
import { AnalyticsPage } from './pages/AnalyticsPage.js'
import { BrandingPage } from './pages/BrandingPage.js'
import { BranchesPage } from './pages/BranchesPage.js'
import { StaffPage } from './pages/StaffPage.js'
import { ProgramPage } from './pages/ProgramPage.js'
import { ProgramBuilderPage } from './pages/ProgramBuilderPage.js'
import { EventDetailPage } from './pages/EventDetailPage.js'
import { StampPage } from './pages/StampPage.js'
import { OwnerEnrollPage } from './pages/OwnerEnrollPage.js'
import { PosterPage } from './pages/PosterPage.js'
import { BroadcastsPage } from './pages/BroadcastsPage.js'
import { CustomersPage } from './pages/CustomersPage.js'
import { LogsPage } from './pages/LogsPage.js'
import { ReferralsPage } from './pages/ReferralsPage.js'
import { SecretMenuPage } from './pages/SecretMenuPage.js'
import { SupportPage } from './pages/SupportPage.js'
import './theme/ownerTokens.css'

// One QueryClient for the whole owner tree. Refetch-on-focus on by default in
// v5; mutations invalidate their own keys explicitly.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: true, retry: 1 },
  },
})

/**
 * Owner sub-application. Mounted by the top-level router for every non-admin,
 * non-staff path. Owns the QueryClient, the `.owner-root` theme scope, and the
 * toast surface.
 *
 * Routes are flat (no tenant slug) — tenant context comes from the session:
 *   /login            public — owner login
 *   /setup/:token     public — setup wizard (token entry)
 *   /setup/continue   gated  — wizard resume for an authenticated owner
 *   /dashboard        gated  — owner shell + dashboard
 *   /branding         gated  — owner shell + branding
 */
export function OwnerApp() {
  // Apply the saved owner theme (data-owner-theme on <html>) tree-wide,
  // including the public login + setup routes.
  useApplyOwnerTheme()

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup/continue" element={<SetupWizardPage resume />} />
        <Route path="/setup/:token" element={<SetupWizardPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* Shell-wrapped authenticated routes. */}
        <Route
          element={
            <OwnerRoute>
              <OwnerShell />
            </OwnerRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/owner/analytics" element={<AnalyticsPage />} />
          <Route path="/branding" element={<BrandingPage />} />
          <Route path="/owner/branches" element={<BranchesPage />} />
          <Route path="/owner/staff" element={<StaffPage />} />
          <Route path="/owner/program" element={<ProgramPage />} />
          <Route path="/owner/referrals" element={<ReferralsPage />} />
          <Route path="/owner/secret-menu" element={<SecretMenuPage />} />
          <Route
            path="/owner/program/:id/builder"
            element={<ProgramBuilderPage />}
          />
          <Route path="/owner/events/:id" element={<EventDetailPage />} />
          <Route path="/owner/stamp" element={<StampPage />} />
          <Route path="/owner/enroll" element={<OwnerEnrollPage />} />
          <Route path="/owner/broadcasts" element={<BroadcastsPage />} />
          <Route path="/owner/customers" element={<CustomersPage />} />
          <Route path="/owner/logs" element={<LogsPage />} />
          <Route path="/owner/support" element={<SupportPage />} />
          <Route path="/owner/poster" element={<PosterPage />} />
          <Route path="/owner/branches/:id/poster" element={<PosterPage />} />
        </Route>

        {/* Root + unknown owner paths → dashboard (the gate redirects to
            /login if there's no session). */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <Toaster
        position="top-center"
        richColors
        offset="calc(env(safe-area-inset-top, 0px) + 16px)"
      />
    </QueryClientProvider>
  )
}
